import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useEffect } from 'react';
import { isEmbeddedMode } from '../auth/embedMode';
import { openAppWindow, toV3ShellHref } from '../util/parentShell';
import { runSharedRedirectFromCfmHref } from '../util/sharedRedirectFromCfmHref';

type LinkType = 'SHARED_REDIRECT' | 'NORMAL' | null;

/**
 * Anchors (or an ancestor) with this attribute skip getSharedRedirect and use native navigation.
 * Ignored when href contains index.cfm — those always use the shared-redirect flow.
 */
export const SKIP_SHARED_REDIRECT_ATTR = 'data-skip-shared-redirect';

interface LinkInterceptorContextType {
  checkHrefType: (href: string | null) => LinkType;
}

const LinkInterceptorContext = createContext<
  LinkInterceptorContextType | undefined
>(undefined);

interface ProviderProps {
  children: ReactNode;
}

const checkHrefType = (href: string | null): LinkType => {
  if (!href) return null;
  // Treat links containing index.cfm as shared-redirect links
  return href.includes('index.cfm') ? 'SHARED_REDIRECT' : 'NORMAL';
};

/** True for http(s) URLs that are not this app origin (e.g. gov.bm PDFs). */
export function isExternalHttpHref(href: string): boolean {
  try {
    const url = new URL(href, globalThis.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return url.origin !== globalThis.location.origin;
  } catch {
    return false;
  }
}

export const LinkInterceptorProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const anchor = target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');

      if (
        anchor.closest(`[${SKIP_SHARED_REDIRECT_ATTR}]`) &&
        !href?.includes('index.cfm')
      ) {
        return;
      }

      const result = checkHrefType(href);

      if (result === 'SHARED_REDIRECT' && href) {
        // For index.cfm links: stop the browser's default navigation
        event.preventDefault();

        void runSharedRedirectFromCfmHref(href, { kind: 'new-tab' });
        return;
      }

      // A link's own onClick that already called preventDefault() has handled the
      // click itself (e.g. PBSourceFileLinks opens the file with window.open, which
      // is already routed to the V3 shell). Opening it here as well gave two tabs
      // for the same file on Ctrl/Cmd/Shift+click.
      if (event.defaultPrevented) return;

      if (result !== 'NORMAL' || !href) return;

      if (
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.button === 1
      ) {
        const shellHref = toV3ShellHref(href);
        if (shellHref) {
          event.preventDefault();
          openAppWindow(href);
        }
        return;
      }

      // V2+V3: plain click on an external URL must not navigate the iframe —
      // third-party hosts (e.g. gov.bm PDFs) refuse framing, so the link
      // appears broken. Open a top-level tab instead (standalone V2 unchanged).
      if (
        isEmbeddedMode() &&
        isExternalHttpHref(href) &&
        anchor.getAttribute('target') !== '_blank'
      ) {
        event.preventDefault();
        openAppWindow(href);
      }
    };

    const handleContextMenu = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || checkHrefType(href) !== 'NORMAL') return;
      const shellHref = toV3ShellHref(href);
      if (!shellHref || shellHref === href) return;
      anchor.setAttribute('href', shellHref);
      const restore = () => {
        if (anchor.getAttribute('href') === shellHref) {
          anchor.setAttribute('href', href);
        }
        document.removeEventListener('click', restore, true);
      };
      setTimeout(() => document.addEventListener('click', restore, true), 0);
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('auxclick', handleClick);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('auxclick', handleClick);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  return (
    <LinkInterceptorContext.Provider value={{ checkHrefType }}>
      {children}
    </LinkInterceptorContext.Provider>
  );
};

export const useLinkInterceptor = (): LinkInterceptorContextType => {
  const context = useContext(LinkInterceptorContext);

  if (!context) {
    throw new Error(
      'useLinkInterceptor must be used within LinkInterceptorProvider'
    );
  }

  return context;
};
